import { create } from 'zustand';
import apiClient from '../api/api';
import toast from 'react-hot-toast';
import { useAuthStore } from './useAuthStore';

// Centralizamos los datos de navegación para que sean consistentes en toda la app.
const socialLinks = [
  { name: 'Noticias', path: '/news', icon: 'fas fa-rss', color: 'text-orange-500', protected: false },
  { name: 'Mi Perfil', path: (user) => {
    if (!user) return '/auth';
    const hasBloggerRole = false;
    const hasActiveTools = user.creatorToolsActive === undefined ? hasBloggerRole : user.creatorToolsActive;
    return hasBloggerRole && hasActiveTools ? `/blogger/${user.username}` : `/profile/${user.username}`;
  }, icon: 'fas fa-user-circle', color: 'text-blue-500', protected: true },
  { name: 'Feed Social', path: '/feed', icon: 'fas fa-newspaper', color: 'text-purple-500', protected: true },
  { name: 'Mensajes', path: '/messenger', icon: 'fas fa-envelope', color: 'text-cyan-500', protected: true, badgeKey: 'messages' },
  { name: 'Notificaciones', path: '/notifications', icon: 'fas fa-bell', color: 'text-red-500', protected: true, badgeKey: 'notifications' },
  { name: 'Explorar', path: '/explore', icon: 'fas fa-users', color: 'text-teal-500', protected: true },
  { name: 'Bloggers', path: '/bloggers', icon: 'fas fa-feather-alt', color: 'text-pink-500', protected: false },
  { name: 'Ágora Central', path: '/agora-central', icon: 'fas fa-crown', color: 'text-[#D4AF37]', protected: false },
  { name: 'Podcasts', path: '/podcasts', icon: 'fas fa-microphone-alt', color: 'text-[#D4AF37]', protected: false },
];

const newsCategories = [
  { name: 'Noticias', path: '/news', icon: 'fas fa-rss', color: 'text-orange-500' },
];

const initialSettings = {
  site: { instant: true, sound: true, showMessageText: true },
  browser: { enabled: false },
  feedback: {
    likes: true, shares: true, comments: true, commentReplies: true,
    mentions: true, wallPosts: true, related: true, storyReplies: true,
    storyQuestions: true, pollVotes: true, coauthorship: true,
  },
  events: {
    mentions: true, friendRequests: true, followers: true, suggestions: true,
    communityInvites: true, photoTags: true, birthdays: true,
    upcomingEvents: true, interestingContent: true,
    recommendedCommunities: true, messageRequests: true,
    interestingClips: true, updates: true,
  },
};

const initialState = {
  sidebar: { isOpen: false },
  modals: {
    createPost: { isOpen: false, data: null },
    uploadPhotos: { isOpen: false, data: null },
    photoSelection: { isOpen: false, data: null },
    createStory: { isOpen: false, data: null },
    storyViewer: { isOpen: false, data: null },
    commentModal: { isOpen: false, data: null },
    forwardPost: { isOpen: false, data: null },
    requestTaskClosure: { isOpen: false, data: null },
    validateTaskClosure: { isOpen: false, data: null },
    report: { isOpen: false, data: null },
    createTaskModal: { isOpen: false, data: null },
    uploadIdentityDocument: { isOpen: false, data: null },
    profileFaqs: { isOpen: false, data: null },
  },
  navigation: { socialLinks, newsCategories },
  settings: { settings: initialSettings, isLoading: false },
  search: { searchTerm: '', suggestions: [], isSearching: false },
  weather: { weatherData: null, forecastData: null, isLoading: true, error: null },
  holidays: { specialDay: null, specialDayType: null, isLoading: true },
  todo: { tasks: [], isLoading: false, error: null, currentPage: 1, totalPages: 1, hasFetched: false },
  mediaPlayer: { isOpen: false, media: null },
  musicPlayer: {
    currentTrack: null,
    playlist: [],
    currentIndex: -1,
    isPlaying: false,
    progress: 0,
    duration: 0,
    currentTime: 0,
    volume: 1,
    isMuted: false,
    isPlayerVisible: false,
    seekTime: null,
  },
};

export const useUIStore = create((set, get) => ({
  ...initialState,

  // =========================
  // Sidebar
  // =========================
  toggleSidebar: () => set((state) => ({
    sidebar: { ...state.sidebar, isOpen: !state.sidebar.isOpen },
  })),
  closeSidebar: () => set((state) => ({
    sidebar: { ...state.sidebar, isOpen: false },
  })),
  openSidebar: () => set((state) => ({
    sidebar: { ...state.sidebar, isOpen: true },
  })),

  // =========================
  // Modals
  // =========================
  openModal: (modalName, data = null) =>
    set((state) => ({
      modals: { ...state.modals, [modalName]: { isOpen: true, data } },
    })),

  closeModal: (modalName) =>
    set((state) => ({
      modals: { ...state.modals, [modalName]: { isOpen: false, data: null } },
    })),

  // =========================
  // Settings
  // =========================
  initializeSettings: async () => {
    const authState = useAuthStore.getState();
    const currentUser = authState.currentUser;

    if (currentUser?.notificationSettings) {
      const mergedSettings = {
        site: { ...initialSettings.site, ...currentUser.notificationSettings.site },
        browser: { ...initialSettings.browser, ...currentUser.notificationSettings.browser },
        feedback: { ...initialSettings.feedback, ...currentUser.notificationSettings.feedback },
        events: { ...initialSettings.events, ...currentUser.notificationSettings.events },
      };
      set((state) => ({
        settings: { ...state.settings, settings: mergedSettings },
      }));
    }

    try {
      const response = await apiClient.get('/notifications/settings');
      const serverSettings = response.data.settings || response.data;
      if (!serverSettings) return;

      const mergedSettings = {
        site: { ...initialSettings.site, ...serverSettings.site },
        browser: { ...initialSettings.browser, ...serverSettings.browser },
        feedback: { ...initialSettings.feedback, ...serverSettings.feedback },
        events: { ...initialSettings.events, ...serverSettings.events },
      };

      set((state) => ({
        settings: { ...state.settings, settings: mergedSettings },
      }));

      if (currentUser) {
        authState.setCurrentUser({
          ...currentUser,
          notificationSettings: mergedSettings,
        });
      }
    } catch (error) {
      // Silencio: si falla, usamos el cache local del usuario
    }
  },

  updateNotificationSettings: async (newSettings) => {
    set((state) => ({ settings: { ...state.settings, isLoading: true } }));
    try {
      const response = await apiClient.patch('/notifications/settings', newSettings);
      set((state) => ({
        settings: { ...state.settings, settings: response.data.settings, isLoading: false },
      }));

      const authState = useAuthStore.getState();
      const currentUser = authState.currentUser;
      if (currentUser) {
        authState.setCurrentUser({
          ...currentUser,
          notificationSettings: response.data.settings,
        });
      }

      toast.success('Configuración guardada.');
    } catch (error) {
      toast.error('No se pudo guardar la configuración.');
      set((state) => ({ settings: { ...state.settings, isLoading: false } }));
    }
  },

  generateTwoFactorSecret: async () => {
    try {
      const response = await apiClient.post('/auth/2fa/generate');
      return { success: true, qrCodeUrl: response.data.qrCodeUrl };
    } catch (error) {
      toast.error('No se pudo generar el secreto para 2FA.');
      return { success: false };
    }
  },

  verifyTwoFactorToken: async (token) => {
    try {
      await apiClient.post('/auth/2fa/verify', { token });
      const authState = useAuthStore.getState();
      const currentUser = authState.currentUser;
      if (currentUser) {
        authState.setCurrentUser({
          ...currentUser,
          isTwoFactorEnabled: true,
        });
      }
      toast.success('¡Verificación en 2 pasos activada!');
      return { success: true };
    } catch (error) {
      toast.error(error.response?.data?.message || 'El código no es válido.');
      return { success: false };
    }
  },

  // =========================
  // Search
  // =========================
  setSearchTerm: (term) => {
    set((state) => ({ search: { ...state.search, searchTerm: term } }));
    get().fetchSuggestions(term);
  },

  fetchSuggestions: async (term) => {
    if (!term.trim()) {
      set((state) => ({ search: { ...state.search, suggestions: [] } }));
      return;
    }
    set((state) => ({ search: { ...state.search, isSearching: true } }));
    try {
      const response = await apiClient.get(`/users/search?q=${term}`);
      set((state) => ({ search: { ...state.search, suggestions: response.data } }));
    } catch (error) {
      set((state) => ({ search: { ...state.search, suggestions: [] } }));
    } finally {
      set((state) => ({ search: { ...state.search, isSearching: false } }));
    }
  },

  clearSearch: () => {
    set((state) => ({ search: { ...state.search, searchTerm: '', suggestions: [] } }));
  },

  // =========================
  // Weather
  // =========================
  fetchWeather: async () => {
    if (get().weather.weatherData && !get().weather.isLoading) {
      return;
    }
    set((state) => ({ weather: { ...state.weather, isLoading: true, error: null } }));
    try {
      const response = await apiClient.get('/weather');
      set((state) => ({
        weather: {
          ...state.weather,
          weatherData: response.data.weather,
          forecastData: response.data.forecast,
          isLoading: false,
        },
      }));
    } catch (err) {
      const message = err.response?.data?.message || 'No se pudo cargar el clima.';
      set((state) => ({ weather: { ...state.weather, isLoading: false, error: message } }));
    }
  },


  // =========================
  // Holidays
  // =========================
  fetchSpecialDay: async (countryCode) => {
    set((state) => ({ holidays: { ...state.holidays, isLoading: true } }));
    try {
      const response = await apiClient.get(`/holidays/${countryCode || 'US'}`);
      set((state) => ({
        holidays: {
          ...state.holidays,
          specialDay: response.data.specialDay,
          specialDayType: response.data.specialDayType,
          isLoading: false,
        },
      }));
    } catch (error) {
      set((state) => ({
        holidays: { ...state.holidays, isLoading: false, specialDay: null, specialDayType: null },
      }));
    }
  },

  // =========================
  // Todo
  // =========================
  fetchTasks: async (page = 1) => {
    set((state) => ({ todo: { ...state.todo, isLoading: true, hasFetched: true } }));
    try {
      const { currentUser } = useAuthStore.getState();
      if (!currentUser) {
        set((state) => ({ todo: { ...state.todo, isLoading: false } }));
        return;
      }
      const url = currentUser.role === 'technician' ? '/todos/my' : '/todos';

      const response = await apiClient.get(url, {
        params: { page, limit: 10 },
      });

      const tasks = Array.isArray(response.data) ? response.data : (response.data.tasks || []);
      const pagination = response.data.pagination || { currentPage: 1, totalPages: 1 };

      set((state) => ({
        todo: {
          ...state.todo,
          tasks,
          currentPage: pagination.currentPage,
          totalPages: pagination.totalPages,
          isLoading: false,
        },
      }));
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'No se pudieron cargar las tareas.';
      toast.error(errorMessage);
      set((state) => ({
        todo: { ...state.todo, error: errorMessage, isLoading: false },
      }));
    }
  },

  updateTaskStatus: async (taskId, status, solution = null) => {
    try {
      const response = await apiClient.patch(`/todos/${taskId}/status`, { status, solution });
      const updatedTask = response.data;
      set((state) => ({
        todo: {
          ...state.todo,
          tasks: state.todo.tasks.map((task) => task._id === taskId ? updatedTask : task),
        },
      }));
      toast.success(`Tarea actualizada a "${status}"`);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'No se pudo actualizar la tarea.';
      toast.error(errorMessage);
      set((state) => ({
        todo: { ...state.todo, isLoading: false, error: errorMessage },
      }));
    }
  },

  addTask: async (description) => {
    try {
      const response = await apiClient.post('/todos', { description });
      const newTask = response.data;
      set((state) => ({
        todo: { ...state.todo, tasks: [newTask, ...state.todo.tasks] },
      }));
      toast.success('Nueva tarea creada.');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'No se pudo crear la tarea.';
      toast.error(errorMessage);
      set((state) => ({
        todo: { ...state.todo, isLoading: false, error: errorMessage },
      }));
    }
  },

  // =========================
  // Media Player
  // =========================
  playMedia: (mediaToPlay) => {
    if (mediaToPlay?.url) {
      set((state) => ({
        mediaPlayer: { ...state.mediaPlayer, media: mediaToPlay, isOpen: true },
      }));
    }
  },

  closePlayer: () => {
    set((state) => ({
      mediaPlayer: { ...state.mediaPlayer, isOpen: false, media: null },
    }));
  },

  // =========================
  // Music Player
  // =========================
  playTrack: (track, playlist, index) => {
    const { currentTrack, isPlaying } = get().musicPlayer;

    if (currentTrack?._id === track._id && isPlaying) {
      get().stopTrack();
    } else {
      set((state) => ({
        musicPlayer: {
          ...state.musicPlayer,
          currentTrack: track,
          playlist: playlist || [track],
          currentIndex: index !== undefined ? index : 0,
          isPlaying: true,
          isPlayerVisible: true,
        },
      }));
    }
  },

  stopTrack: () => {
    set((state) => ({
      musicPlayer: { ...state.musicPlayer, isPlaying: false },
    }));
  },

  togglePlay: () => {
    set((state) => ({
      musicPlayer: { ...state.musicPlayer, isPlaying: !state.musicPlayer.isPlaying },
    }));
  },

  resetPlayer: () => {
    set((state) => ({
      musicPlayer: { ...state.musicPlayer, currentTrack: null, playlist: [], currentIndex: -1, isPlaying: false },
    }));
  },

  playNext: () => {
    const { playlist, currentIndex } = get().musicPlayer;
    if (playlist.length > 0) {
      const nextIndex = (currentIndex + 1) % playlist.length;
      get().playTrack(playlist[nextIndex], playlist, nextIndex);
    }
  },

  playPrev: () => {
    const { playlist, currentIndex } = get().musicPlayer;
    if (playlist.length > 0) {
      const prevIndex = (currentIndex - 1 + playlist.length) % playlist.length;
      get().playTrack(playlist[prevIndex], playlist, prevIndex);
    }
  },

  showPlayer: () => set((state) => ({
    musicPlayer: { ...state.musicPlayer, isPlayerVisible: true },
  })),

  hidePlayer: () => set((state) => ({
    musicPlayer: { ...state.musicPlayer, isPlayerVisible: false },
  })),

  updateProgress: (progress, currentTime, duration) => {
    set((state) => ({
      musicPlayer: { ...state.musicPlayer, progress, currentTime, duration },
    }));
  },

  setVolume: (volume) => set((state) => ({
    musicPlayer: { ...state.musicPlayer, volume, isMuted: false },
  })),

  toggleMute: () => set((state) => ({
    musicPlayer: { ...state.musicPlayer, isMuted: !state.musicPlayer.isMuted },
  })),

  seekTo: (time) => set((state) => ({
    musicPlayer: { ...state.musicPlayer, seekTime: time },
  })),
}));

export const useSidebarStore = useUIStore;
export const useModalStore = useUIStore;
export const useNavigationStore = useUIStore;
export const useSettingsStore = useUIStore;
export const useSearchStore = useUIStore;
export const useWeatherStore = useUIStore;
export const useHolidaysStore = useUIStore;
export const useTodoStore = useUIStore;
export const useMediaPlayerStore = useUIStore;
export const useMusicPlayerStore = useUIStore;
